import { useRef, useState } from 'react';
import * as React from 'react';
import DatePicker from 'react-datepicker';
import type { TextInputBaseProps } from '@sdk/components/primitives/Input';
import { INPUT_HEIGHT } from '@sdk/components/forms/constants';
import type { FormFieldBaseProps } from '@sdk/components/forms/types';

import DatePickerCustomHeader from '@web/forms/components/datePicker/DatePickerCustomHeader/DatePickerCustomHeader';
import { DatePickerFieldContainer } from '@web/forms/components/datePicker/DatePickerField/datePickerField.styles';
import { useDatePickerField } from '@web/forms/components/datePicker/DatePickerField/datePickerField.hooks';
import CustomDatePickerInput from '@web/forms/components/datePicker/DatePickerCustomInput/DatePickerCustomInput';
import {
  Weekday,
  OutputDateFormatType,
  ViewMode,
} from '@web/forms/components/datePicker/datePicker.types';

type Props = {
  date: Date;
  firstDayOfTheWeek?: Weekday;
  height?: number;
  label?: string;
  outputFormat?: OutputDateFormatType;
  withoutYearPicker?: boolean;
  customWidth?: number;
  alwaysOpenOnToday?: boolean;
  onDateChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  skipErrorMessage?: boolean;
  clearable?: boolean;
} & TextInputBaseProps &
  FormFieldBaseProps;

const DatePickerField: React.FC<React.PropsWithChildren<Props>> = ({
  date,
  firstDayOfTheWeek = Weekday.Mon,
  height = INPUT_HEIGHT,
  outputFormat,
  onDateChange,
  withoutYearPicker,
  alwaysOpenOnToday,
  customWidth,
  renderLabel,
  minDate,
  maxDate,
  skipErrorMessage,
  clearable,
  ...props
}) => {
  const datepickerRef = useRef(null);
  const { initialDate, outputDate } = useDatePickerField({
    outputFormat,
    date,
    alwaysOpenOnToday,
  });

  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.day);

  const { invalid, id, label, placeholder, error } = props;

  const minDateDict = {
    [ViewMode.day]: minDate,
    [ViewMode.month]: minDate && new Date(minDate.getFullYear(), minDate.getMonth(), 1),
    [ViewMode.year]: minDate && new Date(minDate.getFullYear(), 0, 1),
  };

  const maxDateDict = {
    [ViewMode.day]: maxDate,
    [ViewMode.month]: maxDate && new Date(maxDate.getFullYear(), maxDate.getMonth(), 1),
    [ViewMode.year]: maxDate && new Date(maxDate.getFullYear(), 0, 1),
  };

  return (
    <DatePickerFieldContainer>
      <DatePicker
        ref={datepickerRef}
        selected={date}
        calendarStartDay={firstDayOfTheWeek}
        openToDate={initialDate}
        customInput={
          <CustomDatePickerInput
            height={height}
            customWidth={customWidth}
            renderLabel={renderLabel}
            skipErrorMessage={skipErrorMessage}
            clearable={clearable}
            outputDate={outputDate}
            invalid={invalid}
            id={id}
            label={label}
            inputPlaceholder={placeholder}
            error={error}
            datepickerRef={datepickerRef}
            date={date}
            onDateChange={onDateChange}
            inputProps={props}
          />
        }
        showMonthYearPicker={viewMode === ViewMode.month}
        showYearPicker={viewMode === ViewMode.year}
        disabledKeyboardNavigation
        onChange={(date: Date): void => {
          if (viewMode === ViewMode.day) {
            onDateChange(date);
          } else if (viewMode === ViewMode.month) {
            setViewMode(ViewMode.day);
            datepickerRef.current.calendar.instanceRef.changeMonth(date.getMonth());
          } else if (viewMode === ViewMode.year) {
            setViewMode(ViewMode.month);
            datepickerRef.current.calendar.instanceRef.changeYear(date.getFullYear());
          }
        }}
        onCalendarOpen={() => {
          setViewMode(ViewMode.day);
        }}
        shouldCloseOnSelect={viewMode === ViewMode.day}
        minDate={minDateDict[viewMode]}
        maxDate={maxDateDict[viewMode]}
        renderCustomHeader={(args) => (
          <DatePickerCustomHeader
            viewMode={viewMode}
            setViewMode={setViewMode}
            withoutYearPicker={withoutYearPicker}
            {...args}
          />
        )}
      />
    </DatePickerFieldContainer>
  );
};

export default DatePickerField;
